import { useRouter } from "next/router";
import { GetEventAndReleaseForCustomerQuery } from "~graphql/sdk";
import {
  CompetitionFragmentFragment,
  CompetitionType,
} from "~graphql/typed-document-nodes";
import {
  usePreviewCompetition,
  useWillShowPreviewCompetition,
} from "~features/events/hooks/usePreviewCompetition";
import { useState } from "react";
import { useOrganization } from "~hooks";

type EventForCustomer = GetEventAndReleaseForCustomerQuery["getEventAndReleaseForCustomer"]["event"];
type ReleaseForCustomer = GetEventAndReleaseForCustomerQuery["getEventAndReleaseForCustomer"]["release"];

// The page can be in one of these states. Note: This is not the same as the state of the event since the event can have unlisted releases which are on sale.

type PageState =
  | "info"
  | "registration"
  | "registration-competition"
  | "registration-presale-competition"
  | "registration-presale"
  | "active-presale"
  | "onsale"
  | "onsale-competition"
  | "registration-presale-pre-purchase"
  | "registration-presale-pre-purchase-competition";

type ModalType =
  | "releaseCodes"
  | "registration"
  | "registration-success"
  | undefined;

interface EventPageContext {
  state: PageState;
  event: EventForCustomer;
  activeModal: ModalType;
  setActiveModal: (modalType: ModalType) => void;
  userEmail?: string;
  setUserEmail: (email: string) => void;
  waitlistUserId?: string;
  setWaitlistUserId: (userId: string) => void;
}

/* ------------------------- Context for each state ------------------------- */
export interface InfoContext extends EventPageContext {
  state: "info";
}

export interface RegistrationContext extends EventPageContext {
  state: "registration";
}

export interface RegistrationPresaleContext extends EventPageContext {
  state: "registration-presale";
  presaleRelease: EventForCustomer["presaleRelease"];
}

export interface ActivePresaleContext extends EventPageContext {
  state: "active-presale";
}

export interface RegistrationPresaleCompetitionContext
  extends EventPageContext {
  state: "registration-presale-competition";
  presaleRelease: EventForCustomer["presaleRelease"];
  competition: CompetitionFragmentFragment;
}

export interface RegistrationCompetitionContext extends EventPageContext {
  state: "registration-competition";
  competition: CompetitionFragmentFragment;
}

export interface OnsaleContext extends EventPageContext {
  state: "onsale";
}

export interface OnsaleCompetitionContext extends EventPageContext {
  state: "onsale-competition";
  competition: CompetitionFragmentFragment;
}

export interface RegistrationPresalePrePurchaseContext
  extends EventPageContext {
  state: "registration-presale-pre-purchase";
  presaleRelease: EventForCustomer["presaleRelease"];
}

export interface RegistrationPresalePrePurchaseCompetitionContext
  extends EventPageContext {
  state: "registration-presale-pre-purchase-competition";
  presaleRelease: EventForCustomer["presaleRelease"];
  competition: CompetitionFragmentFragment;
}

export type PageContext =
  | InfoContext
  | RegistrationContext
  | RegistrationCompetitionContext
  | RegistrationPresaleCompetitionContext
  | RegistrationPresaleContext
  | ActivePresaleContext
  | OnsaleContext
  | OnsaleCompetitionContext
  | RegistrationPresalePrePurchaseContext
  | RegistrationPresalePrePurchaseCompetitionContext;

export type CompetitionPageContext =
  | RegistrationCompetitionContext
  | RegistrationPresaleCompetitionContext
  | OnsaleCompetitionContext
  | RegistrationPresalePrePurchaseCompetitionContext;

function competitionWithSortedPrizes(
  competition: CompetitionFragmentFragment
): CompetitionFragmentFragment {
  return {
    ...competition,
    prizes: competition.prizes.sort((a, b) => a.prizeOrder - b.prizeOrder),
  };
}

/* ------------------------ Logic to determine state ------------------------ */
// Note: A lot of this logic can be moved to BE. We can have a single query that returns the state and the data for that state.
// That way we don't need to fetch all competitions.
const useDetailsPageContext = (
  event: EventForCustomer,
  release: ReleaseForCustomer | undefined, // Release is undefined when there its info or registration only
  competitions: CompetitionFragmentFragment[] = []
): PageContext => {
  const router = useRouter();
  const { competition: previewCompetition } = usePreviewCompetition();
  const showPreviewCompetition = useWillShowPreviewCompetition();
  const isUnlistedRelease = router.query.slug ? true : false;
  const [activeModal, setActiveModal] = useState<ModalType>(undefined);
  const [userEmail, setUserEmail] = useState<string | undefined>(undefined);
  const [waitlistUserId, setWaitlistUserId] = useState<string | undefined>(
    undefined
  );

  const { hasFeature } = useOrganization();

  // On sale
  const areTicketsAvailableForPurchase =
    (release?.isActive && isUnlistedRelease) || event?.hasPublicRelease;
  const onsaleCompetition = competitions.find(
    (c) => c.competitionType === CompetitionType.Onsale
  );
  const isOnsaleCompetitionAvailable = !!onsaleCompetition;

  // Registration
  const isRegistrationAvailable = event.isWaitlistAvailable;
  const isRegistrationLinkedToPresale = !!event.presaleRelease;
  const isPresaleLive = event.presaleRelease?.startConditionDate
    ? new Date(event.presaleRelease.startConditionDate) < new Date()
    : false;
  const registrationCompetition = competitions.find(
    (c) => c.competitionType === CompetitionType.Registration
  );
  const isRegistrationCompetitionAvailable = !!registrationCompetition;
  const isPrePurchaseActive =
    event.presaleRelease?.isPrePurchaseActive &&
    hasFeature("prePurchaseRegistration");

  const commonProps = {
    event,
    activeModal,
    setActiveModal,
    userEmail,
    setUserEmail,
    waitlistUserId,
    setWaitlistUserId,
  };

  if (showPreviewCompetition && previewCompetition) {
    return {
      state:
        previewCompetition.competitionType === CompetitionType.Registration
          ? "registration-competition"
          : "onsale-competition",
      competition: competitionWithSortedPrizes(previewCompetition),
      ...commonProps,
    };
  } else if (areTicketsAvailableForPurchase && !isOnsaleCompetitionAvailable) {
    return {
      state: "onsale",
      ...commonProps,
    };
  } else if (areTicketsAvailableForPurchase && isOnsaleCompetitionAvailable) {
    return {
      state: "onsale-competition",
      competition: competitionWithSortedPrizes(onsaleCompetition),
      ...commonProps,
    };
  } else if (
    isRegistrationAvailable &&
    !isRegistrationCompetitionAvailable &&
    !isRegistrationLinkedToPresale
  ) {
    return {
      state: "registration",
      ...commonProps,
    };
  } else if (
    isRegistrationAvailable &&
    isRegistrationLinkedToPresale &&
    !isPresaleLive &&
    isPrePurchaseActive &&
    isRegistrationCompetitionAvailable
  ) {
    return {
      state: "registration-presale-pre-purchase-competition",
      presaleRelease: event.presaleRelease,
      competition: competitionWithSortedPrizes(registrationCompetition),
      ...commonProps,
    };
  } else if (
    isRegistrationAvailable &&
    isRegistrationLinkedToPresale &&
    !isPresaleLive &&
    isPrePurchaseActive
  ) {
    return {
      state: "registration-presale-pre-purchase",
      presaleRelease: event.presaleRelease,
      ...commonProps,
    };
  } else if (
    isRegistrationAvailable &&
    isRegistrationLinkedToPresale &&
    !isRegistrationCompetitionAvailable &&
    !isPresaleLive
  ) {
    return {
      state: "registration-presale",
      presaleRelease: event.presaleRelease,
      ...commonProps,
    };
  } else if (
    isRegistrationAvailable &&
    isRegistrationLinkedToPresale &&
    isRegistrationCompetitionAvailable &&
    !isPresaleLive
  ) {
    return {
      state: "registration-presale-competition",
      presaleRelease: event.presaleRelease,
      competition: competitionWithSortedPrizes(registrationCompetition),
      ...commonProps,
    };
  } else if (
    isRegistrationAvailable &&
    isRegistrationCompetitionAvailable &&
    !isRegistrationLinkedToPresale
  ) {
    return {
      state: "registration-competition",
      competition: competitionWithSortedPrizes(registrationCompetition),
      ...commonProps,
    };
  } else if (isRegistrationLinkedToPresale && isPresaleLive) {
    return {
      state: "active-presale",
      ...commonProps,
    };
  }

  return {
    state: "info",
    ...commonProps,
  };
};

export default useDetailsPageContext;

export function eventPhaseFromPageContext(
  context: PageContext
): "registration" | "onsale" | "info" | "unknown" {
  if (
    [
      "registration",
      "registration-competition",
      "registration-presale",
      "registration-presale-competition",
    ].includes(context.state)
  ) {
    return "registration";
  }

  if (["onsale", "onsale-competition"].includes(context.state)) {
    return "onsale";
  }

  if (context.state === "info") {
    return "info";
  }

  return "unknown";
}

export function competitionFromPageContext(
  context: PageContext
): CompetitionFragmentFragment | undefined {
  return isCompetitionPageContext(context) ? context.competition : undefined;
}

/* ----------------------------- Type predicates ---------------------------- */
export function isInfoContext(context: PageContext): context is InfoContext {
  return context.state === "info";
}

export function isRegistrationContext(
  context: PageContext
): context is RegistrationContext {
  return context.state === "registration";
}

export function isRegistrationCompetitionContext(
  context: PageContext
): context is RegistrationCompetitionContext {
  return context.state === "registration-competition";
}

export function isRegistrationPresaleContext(
  context: PageContext
): context is RegistrationPresaleContext {
  return context.state === "registration-presale";
}

export function isRegistrationPresaleCompetitionContext(
  context: PageContext
): context is RegistrationPresaleCompetitionContext {
  return context.state === "registration-presale-competition";
}

export function isCompetitionPageContext(
  context: PageContext
): context is CompetitionPageContext {
  return (
    isRegistrationCompetitionContext(context) ||
    isRegistrationPresaleCompetitionContext(context) ||
    isOnsaleCompetitionContext(context) ||
    isRegistrationPresalePrePurchaseCompetitionContext(context)
  );
}

export function isOnsaleContext(
  context: PageContext
): context is OnsaleContext {
  return context.state === "onsale";
}

export function isOnsaleCompetitionContext(
  context: PageContext
): context is OnsaleCompetitionContext {
  return context.state === "onsale-competition";
}

export function isRegistrationPresalePrePurchaseCompetitionContext(
  context: PageContext
): context is RegistrationPresalePrePurchaseCompetitionContext {
  return context.state === "registration-presale-pre-purchase-competition";
}

export function isRegistrationPresalePrePurchaseContext(
  context: PageContext
): context is RegistrationPresalePrePurchaseContext {
  return context.state === "registration-presale-pre-purchase";
}
